import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { useStartGoogleDriveSourceConnect } from '@/hooks';
import { GoogleDriveSourceSafe } from '@vivport/backend/types';
import { startConnectInNewTab } from './start-connect-in-new-tab';

function statusVariant(status: GoogleDriveSourceSafe['status']): 'default' | 'secondary' | 'destructive' | 'outline' {
  if (status === 'ACTIVE') {
    return 'default';
  }
  if (status === 'REVOKED') {
    return 'destructive';
  }
  return 'secondary';
}

export function GoogleDriveSourceDetails({ source, isHubAdmin }: { source: GoogleDriveSourceSafe; isHubAdmin: boolean }) {
  const startConnect = useStartGoogleDriveSourceConnect();

  const reconnect = () => {
    startConnectInNewTab({ label: 'Google Drive', start: () => startConnect.mutateAsync() });
  };

  return (
    <div className="rounded-md border p-4 space-y-3">
      <div className="flex items-center gap-2">
        <span className="font-medium">{source.name}</span>
        <Badge variant={statusVariant(source.status)}>{source.status}</Badge>
      </div>
      <p className="text-sm text-muted-foreground">
        Connected as {source.accountEmail}. If that Google account is removed from your organisation, reconnect with
        another admin.
      </p>
      {source.revokedReason ? (
        <p className="text-sm text-destructive">Access revoked: {source.revokedReason}. Disconnect and connect again.</p>
      ) : null}
      {source.canWrite ? null : (
        <div className="flex items-center justify-between gap-2">
          <p className="text-sm text-muted-foreground">Publishing needs the Drive file scope. Reconnect this account to grant it.</p>
          {isHubAdmin ? (
            <Button type="button" variant="outline" size="sm" onClick={reconnect} disabled={startConnect.isPending}>
              Reconnect
            </Button>
          ) : null}
        </div>
      )}
    </div>
  );
}
